import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Chip,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Usb as UsbIcon,
  Stop as StopIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Memory as MemoryIcon,
  SignalCellular4Bar as SignalCellular4BarIcon,
  Code as CodeIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { SerialPortService, LiveTrainingSession, ParsedScanData } from '../../../services/SerialPortService';
import { ProfileProcessingService } from '../../../services/ProfileProcessingService';
import { ProfileCategory, PROFILE_CATEGORY_INFO, ScanData } from '../../../types/cardReaderProfiles';

interface NewProfileModalProps {
  open: boolean;
  onClose: () => void;
  onProfileCreated: (profileId: string) => void;
}

const steps = [
  'Profile Details',
  'Device Connection',
  'Data Collection',
  'Processing'
];

const NewProfileModal: React.FC<NewProfileModalProps> = ({ open, onClose, onProfileCreated }) => {
  const { userProfile } = useAuth();
  const { colors } = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [profileName, setProfileName] = useState('');
  const [category, setCategory] = useState<ProfileCategory>('dip');
  const [targetScans, setTargetScans] = useState(100);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [trainingSession, setTrainingSession] = useState<LiveTrainingSession | null>(null);
  const [isCollecting, setIsCollecting] = useState(false);
  const [collectedScans, setCollectedScans] = useState<ScanData[]>([]);
  const [lastScanData, setLastScanData] = useState<ParsedScanData | null>(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [serialLog, setSerialLog] = useState<string[]>([]);
  const [showLog, setShowLog] = useState(false);
  const [profileProcessed, setProfileProcessed] = useState(false);
  const profileProcessedRef = useRef(false);

  // Check for Web Serial API support
  const serialSupported = SerialPortService.isSupported();

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (isConnected) {
        SerialPortService.disconnect();
      }
    };
  }, [isConnected]);

  // Subscribe to scan data
  useEffect(() => {
    if (!trainingSession) return;

    const unsubscribe = SerialPortService.onScanData((parsed: ParsedScanData) => {
      console.log(`📡 Received scan: ${parsed.scanId} with ${parsed.sensorReadings.length} readings`);
      setLastScanData(parsed);

      // Validate scan has sufficient data (minimum 50 readings for a valid scan)
      if (parsed.sensorReadings.length < 50) {
        console.log(`⚠️ REJECTED: Scan ${parsed.scanId} with only ${parsed.sensorReadings.length} readings (minimum 50 required)`);
        console.log(`⚠️ SCAN NOT ADDED TO COLLECTION`);
        return;
      }

      console.log(`✅ VALID: Scan ${parsed.scanId} has sufficient readings (${parsed.sensorReadings.length} >= 50)`);


      // Convert to ScanData format
      const scanData = SerialPortService.convertToScanData(parsed, trainingSession.deviceId);

      setCollectedScans(prev => {
        // Check for duplicate scans (same scanId or very similar timestamp)
        const getTimestamp = (timestamp: Date | any): number => {
          if (timestamp instanceof Date) {
            return timestamp.getTime();
          } else if (timestamp && typeof timestamp.toDate === 'function') {
            // Firebase Timestamp
            return timestamp.toDate().getTime();
          } else {
            // Fallback to current time
            return Date.now();
          }
        };

        const isDuplicate = prev.some(existingScan =>
          existingScan.scanId === scanData.scanId ||
          Math.abs(getTimestamp(existingScan.timestamp) - getTimestamp(scanData.timestamp)) < 1000
        );

        if (isDuplicate) {
          console.log(`⚠️ Rejecting duplicate scan: ${scanData.scanId}`);
          return prev; // Don't add duplicate
        }

        const updated = [...prev, scanData];
        console.log(`✅ Added valid scan: ${scanData.scanId} with ${scanData.sensorReadings.length} readings`);

        // Update session
        if (trainingSession) {
          setTrainingSession(session => session ? {
            ...session,
            collectedScans: updated.length,
            scans: updated,
            lastScanTime: new Date()
          } : null);
        }

        // Auto-advance when target reached
        if (updated.length >= targetScans && !profileProcessedRef.current) {
          setIsCollecting(false);
          setActiveStep(3); // Move to processing step
          processTrainingData(updated);
        }

        return updated;
      });
    });

    return unsubscribe;
  }, [trainingSession, targetScans]);

  // Subscribe to serial log messages
  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = SerialPortService.onLogMessage((message: string) => {
      setSerialLog(prev => {
        const updated = [...prev, message];
        // Keep ALL log entries (no limit)
        return updated;
      });
    });

    return unsubscribe;
  }, [isConnected]);

  const handleConnect = async () => {
    try {
      setConnectionError(null);
      const success = await SerialPortService.connect();
      if (success) {
        setIsConnected(true);
        setActiveStep(2); // Move to data collection step
      }
    } catch (error: any) {
      setConnectionError(error.message);
      console.error('Connection failed:', error);
    }
  };

  const handleDisconnect = async () => {
    try {
      await SerialPortService.exitTrainingMode();
      await SerialPortService.disconnect();
      setIsConnected(false);
    } catch (error) {
      console.error('Disconnect error:', error);
    }
  };

  const handleStartCollection = async () => {
    if (!profileName.trim()) {
      alert('Please enter a profile name');
      return;
    }

    try {
      const session = await SerialPortService.startTrainingSession(
        profileName.trim(),
        category,
        targetScans
      );

      setTrainingSession(session);
      setIsCollecting(true);
      setCollectedScans([]);

    } catch (error: any) {
      alert(`Failed to start training: ${error.message}`);
    }
  };

  const handleStopCollection = async () => {
    setIsCollecting(false);
    await SerialPortService.exitTrainingMode();

    if (collectedScans.length > 0 && !profileProcessedRef.current) {
      setActiveStep(3); // Move to processing step
      processTrainingData(collectedScans);
    }
  };

  const processTrainingData = async (scans: ScanData[]) => {
    if (!userProfile?.masterOwnerID) return;

    // Prevent duplicate processing
    if (profileProcessed || profileProcessedRef.current) {
      console.log('⚠️ Profile already processed, ignoring duplicate call');
      return;
    }

    setProfileProcessed(true);
    profileProcessedRef.current = true;
    setIsProcessing(true);
    setProcessingProgress(0);

    try {
      // Step 1: Validate scan data
      setProcessingProgress(10);
      const validation = ProfileProcessingService.validateTrainingScans(scans);
      if (!validation.valid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      // Step 2: Process scans into profile using real FlowMatch processing
      setProcessingProgress(30);
      const profile = await ProfileProcessingService.processTrainingScans(
        scans,
        profileName.trim(),
        category,
        userProfile.masterOwnerID,
        userProfile.email || userProfile.masterOwnerID || 'unknown'
      );

      setProcessingProgress(100);
      setIsProcessing(false);

      console.log('✅ Profile created successfully:', profile);
      onProfileCreated(profile.id);

      // Small delay to show completion before closing
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error: any) {
      console.error('Processing error:', error);
      setIsProcessing(false);
      alert(`Failed to create profile: ${error.message}`);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setProfileName('');
    setCategory('dip');
    setTargetScans(100);
    setIsConnected(false);
    setConnectionError(null);
    setTrainingSession(null);
    setIsCollecting(false);
    setCollectedScans([]);
    setLastScanData(null);
    setProcessingProgress(0);
    setIsProcessing(false);
    setSerialLog([]);
    setShowLog(false);
    setProfileProcessed(false);
    profileProcessedRef.current = false;
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Profile Details
        return (
          <Box>
            <TextField
              fullWidth
              label="Profile Name"
              value={profileName}
              onChange={(e) => setProfileName(e.target.value)}
              placeholder="e.g., Bank ATM DIP Reader"
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  color: colors.TextPrimary,
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: colors.Info },
                  '&.Mui-focused fieldset': { borderColor: colors.Info },
                },
                '& .MuiInputLabel-root': { color: colors.TextSecondary },
                '& .MuiInputLabel-root.Mui-focused': { color: colors.Info }
              }}
              required
            />

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ color: colors.TextSecondary, '&.Mui-focused': { color: colors.Info } }}>Reader Category</InputLabel>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value as ProfileCategory)}
                label="Reader Category"
                sx={{
                  color: colors.TextPrimary,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.Info },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.Info },
                  '& .MuiSvgIcon-root': { color: colors.TextSecondary }
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: 'rgba(10, 10, 10, 0.95)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }
                  }
                }}
              >
                {Object.entries(PROFILE_CATEGORY_INFO).map(([key, info]) => (
                  <MenuItem key={key} value={key} sx={{ color: colors.TextPrimary, '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' } }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>{info.icon}</span>
                      <Box>
                        <Typography variant="body1">{info.displayName}</Typography>
                        <Typography variant="caption" sx={{ color: colors.TextSecondary }}>
                          {info.description}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label="Target Scans"
              value={targetScans}
              onChange={(e) => setTargetScans(Math.max(1, parseInt(e.target.value) || 100))}
              InputProps={{ inputProps: { min: 1, max: 500 } }}
              helperText="Minimum: 1 scan, Recommended: 100 scans for optimal accuracy"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: colors.TextPrimary,
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: colors.Info },
                  '&.Mui-focused fieldset': { borderColor: colors.Info },
                },
                '& .MuiInputLabel-root': { color: colors.TextSecondary },
                '& .MuiInputLabel-root.Mui-focused': { color: colors.Info },
                '& .MuiFormHelperText-root': { color: colors.TextSecondary }
              }}
            />
          </Box>
        );

      case 1: // Device Connection
        return (
          <Box>
            {!serialSupported ? (
              <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(211, 47, 47, 0.1)', color: '#ffb4ab', border: '1px solid rgba(211, 47, 47, 0.3)' }}>
                Web Serial API is not supported in this browser.
                Please use Chrome, Edge, or another Chromium-based browser.
              </Alert>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 3, bgcolor: 'rgba(34, 211, 238, 0.05)', color: colors.Info, border: '1px solid rgba(34, 211, 238, 0.2)' }}>
                  Connect your SkimBuster device via USB. Make sure the device is powered on
                  and the drivers are installed.
                </Alert>

                {connectionError && (
                  <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(211, 47, 47, 0.1)', color: '#ffb4ab', border: '1px solid rgba(211, 47, 47, 0.3)' }}>
                    Connection Error: {connectionError}
                  </Alert>
                )}

                <Box display="flex" justifyContent="center" mb={3}>
                  {!isConnected ? (
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<UsbIcon />}
                      onClick={handleConnect}
                      disabled={!serialSupported}
                      sx={{ bgcolor: colors.Info, color: '#000', '&:hover': { bgcolor: colors.LightAccentHover } }}
                    >
                      Connect Device
                    </Button>
                  ) : (
                    <Box textAlign="center">
                      <CheckIcon sx={{ fontSize: 48, color: colors.Info, mb: 1 }} />
                      <Typography variant="h6" sx={{ color: colors.Info }}>
                        Device Connected
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={handleDisconnect}
                        sx={{ mt: 1, color: colors.TextSecondary, borderColor: 'rgba(255, 255, 255, 0.2)', '&:hover': { borderColor: colors.TextPrimary, color: colors.TextPrimary } }}
                      >
                        Disconnect
                      </Button>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        );

      case 2: // Data Collection
        return (
          <Box>
            <Paper sx={{
              p: 2,
              mb: 3,
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              boxShadow: 'none'
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" sx={{ color: colors.TextPrimary }}>
                  Training: {profileName}
                </Typography>
                <Chip
                  label={PROFILE_CATEGORY_INFO[category]?.displayName || category}
                  sx={{ bgcolor: 'rgba(34, 211, 238, 0.1)', color: colors.Info, border: `1px solid rgba(34, 211, 238, 0.2)` }}
                  icon={<span style={{ color: colors.Info }}>{PROFILE_CATEGORY_INFO[category]?.icon || '🏧'}</span>}
                />
              </Box>

              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography variant="h4" sx={{ color: colors.Info }}>
                  {collectedScans.length}
                </Typography>
                <Typography variant="body1" sx={{ color: colors.TextSecondary }}>
                  / {targetScans} scans collected
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={(collectedScans.length / targetScans) * 100}
                sx={{
                  mb: 2,
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': { backgroundColor: colors.Info }
                }}
              />

              <Box display="flex" justifyContent="center" gap={2}>
                {!isCollecting ? (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<PlayIcon />}
                    onClick={handleStartCollection}
                    disabled={!profileName.trim()}
                    sx={{ bgcolor: colors.Info, color: '#000', '&:hover': { bgcolor: colors.LightAccentHover } }}
                  >
                    Start Collection
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<StopIcon />}
                    onClick={handleStopCollection}
                    color="error"
                    sx={{ borderColor: 'rgba(211, 47, 47, 0.5)', color: '#ffb4ab', '&:hover': { borderColor: '#ffb4ab', bgcolor: 'rgba(211, 47, 47, 0.1)' } }}
                  >
                    Stop Collection
                  </Button>
                )}
              </Box>
            </Paper>

            {/* Real-time scan display */}
            {lastScanData && (
              <Paper sx={{
                p: 2,
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                boxShadow: 'none'
              }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: colors.TextPrimary }}>
                  Latest Scan Data
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <MemoryIcon fontSize="small" sx={{ color: colors.Info }} />
                    <Typography variant="body2" sx={{ color: colors.TextSecondary }}>
                      Samples: {lastScanData.sensorReadings.length}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SignalCellular4BarIcon fontSize="small" sx={{ color: colors.Warning }} />
                    <Typography variant="body2" sx={{ color: colors.TextSecondary }}>
                      Duration: {lastScanData.duration}ms
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}

            {isCollecting && (
              <Alert severity="info" sx={{ mt: 2 }}>
                🎯 Ready to collect scans! Insert and remove cards from the reader to generate training data.
                Each insertion should produce one scan.
              </Alert>
            )}

            {/* Serial Port Log */}
            {isConnected && (
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CodeIcon fontSize="small" />
                    <Typography variant="subtitle1">
                      Serial Port Log ({serialLog.length} messages)
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: 'grey.900',
                      color: 'common.white',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      maxHeight: 300,
                      overflowY: 'auto'
                    }}
                  >
                    {serialLog.length === 0 ? (
                      <Typography variant="body2" sx={{ color: 'grey.400' }}>
                        No serial data received yet...
                      </Typography>
                    ) : (
                      serialLog.map((message, index) => (
                        <div key={index}>
                          {message}
                        </div>
                      ))
                    )}
                  </Paper>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                    <Typography variant="caption" color="text.secondary">
                      Showing all {serialLog.length} messages
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setSerialLog([])}
                      disabled={serialLog.length === 0}
                    >
                      Clear Log
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        );

      case 3: // Processing
        return (
          <Box textAlign="center">
            {isProcessing ? (
              <>
                <Typography variant="h6" gutterBottom>
                  Processing {collectedScans.length} scans...
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={processingProgress}
                  sx={{ mb: 2, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {processingProgress.toFixed(0)}% complete
                </Typography>
              </>
            ) : (
              <>
                <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Profile Created Successfully!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your new profile "{profileName}" has been created with {collectedScans.length} training scans.
                </Typography>
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return profileName.trim().length > 0;
      case 1:
        return isConnected;
      case 2:
        return collectedScans.length >= 1; // Minimum scans
      default:
        return false;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isCollecting}
      PaperProps={{
        sx: {
          background: 'rgba(255, 255, 255, 0.03)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(34, 211, 238, 0.25)',
          borderTop: '1px solid rgba(34, 211, 238, 0.5)',
          borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
          borderRadius: '20px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
          color: colors.TextPrimary
        }
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "'Inter', sans-serif",
          color: '#ffffff',
          fontSize: '15px',
          fontWeight: 700,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          padding: '20px 24px',
          borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
          backgroundColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Typography variant="h5" sx={{ textTransform: 'none', letterSpacing: 'normal', fontSize: '1.25rem' }}>Create New Profile</Typography>
          <IconButton onClick={onClose} disabled={isCollecting} sx={{ color: colors.TextSecondary, '&:hover': { color: colors.TextPrimary } }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
        <Stepper activeStep={activeStep} sx={{
          mb: 3,
          '& .MuiStepLabel-label': { color: colors.TextSecondary },
          '& .MuiStepLabel-label.Mui-active': { color: colors.Info },
          '& .MuiStepLabel-label.Mui-completed': { color: colors.TextPrimary },
          '& .MuiStepIcon-root': { color: 'rgba(255, 255, 255, 0.2)' },
          '& .MuiStepIcon-root.Mui-active': { color: colors.Info },
          '& .MuiStepIcon-root.Mui-completed': { color: colors.Info }
        }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Divider sx={{ mb: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
        <Button
          onClick={handleReset}
          variant="outlined"
          sx={{
            color: '#ffffff',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50px',
            fontWeight: 'bold',
            px: 2,
            '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
          }}
        >
          Reset
        </Button>

        {activeStep > 0 && activeStep < 3 && (
          <Button
            onClick={() => setActiveStep(prev => prev - 1)}
            variant="outlined"
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
            }}
          >
            Back
          </Button>
        )}

        {activeStep < 1 && (
          <Button
            variant="outlined"
            onClick={() => setActiveStep(prev => prev + 1)}
            disabled={!canProceed()}
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(34, 211, 238, 0.5)',
              background: 'rgba(34, 211, 238, 0.15)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
              '&:disabled': { borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.05)' },
            }}
          >
            Next
          </Button>
        )}

        {activeStep === 3 && !isProcessing && (
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(34, 211, 238, 0.5)',
              background: 'rgba(34, 211, 238, 0.15)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
            }}
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default NewProfileModal;